import { useCustomToast } from '@/hooks/use-custom-toast';
import { useCallback, useMemo, memo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  addLocationGoogleReviewValidationSchema,
  TAddLocationGoogleReviewValidation,
  useAddLocationGoogleReview,
} from '../../../api/add-location-google-review.api';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LOCATION_GOOGLE_REVIEW_QUERY_KEY } from '../../../api/get-list-location-google-review.api';
import { handleDomBubbleEvent } from '@/utils/dom';
import { cn } from '@/utils/cn';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import { useQueryState } from 'nuqs';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

export type TAddLocationGoogleReviewModalType = 'AddLocationGoogleReviewModal';

export type TLocation = {
  name: string;
  title: string;
};

export interface IAddLocationGoogleReviewModalProps {
  state?: string;
  locations?: TLocation[];
}

const AddLocationGoogleReviewModal = memo(({ state, locations }: IAddLocationGoogleReviewModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const navigate = useNavigate();
  const channelModalRootActions = useChannelModalRootActionsStore();

  const [googleReviewState, _setGoogleReviewState] = useQueryState('state', {
    defaultValue: '',
    history: 'push',
    parse: (value) => value,
    serialize: (value) => value,
  });

  const form = useForm<TAddLocationGoogleReviewValidation>({
    mode: 'onChange',
    resolver: zodResolver(addLocationGoogleReviewValidationSchema),
  });

  const mutateAddLocationGoogleReview = useAddLocationGoogleReview({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Failed to add google review location';
        showToast({ message: errorMessage, type: 'error' });
      },
      onSuccess: () => {
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [GET_LIST_LOCATION_GOOGLE_REVIEW_QUERY_KEY],
          }),
        ]);
        channelModalRootActions.closeModal('AddLocationGoogleReviewModal');
        navigate('/channel-integrations/google-review');
        showToast({ message: 'Google review location added successfully', type: 'success' });
      },
    },
  });

  useEffect(() => {
    if (state || googleReviewState) {
      form.reset({
        state: state || googleReviewState || '',
        names: [],
      });
    }
  }, [state, googleReviewState, form]);

  // HELPERS
  const selectedNames = form.watch('names');

  const isDisabledButton = useMemo(() => {
    return mutateAddLocationGoogleReview.isPending || selectedNames?.length === 0;
  }, [mutateAddLocationGoogleReview.isPending, selectedNames]);

  // HANDLERS
  const handleSubmitForm = useCallback(() => {
    const formState = form.getValues();
    if (!formState.state || formState?.names?.length === 0) {
      showToast({
        message: 'Please select a state and at least one location',
        type: 'error',
      });
      return;
    }
    mutateAddLocationGoogleReview.mutate({
      state: formState.state,
      names: formState.names,
    });
  }, [mutateAddLocationGoogleReview, form, showToast]);

  const isOpenAddLocationGoogleReviewModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('AddLocationGoogleReviewModal');
  }, [channelModalRootActions]);

  const handleCloseModal = useCallback(() => {
    channelModalRootActions.closeModal('AddLocationGoogleReviewModal');
  }, [channelModalRootActions]);

  return (
    <Modal
      isOpen={isOpenAddLocationGoogleReviewModal}
      lockBodyScroll
      onClose={handleCloseModal}
      isCloseOutside={false}
      className="xl:w-[500px] w-[90vw] p-[24px]"
    >
      <ModalHeader>
        <ModalTitle>Add Location</ModalTitle>
      </ModalHeader>
      <ModalContent className="max-h-[280px]">
        <form
          id="add-location-google-review-form"
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            e.preventDefault();
            handleSubmitForm();
          }}
          className="gap-[6px] flex flex-col"
        >
          <span className="text-[14px] font-normal text-[#25151E]">Total Location: {locations?.length || 0}</span>

          <div className="overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-[#D9D9D9] scrollbar-track-transparent">
            {locations?.map((item: TLocation, index: number) => (
              <Controller
                key={`${item.name}-${index}`}
                control={form.control}
                name="names"
                defaultValue={[]}
                render={({ field }) => {
                  const value = field.value || [];
                  const isChecked = value.includes(item.name);

                  const toggle = () => {
                    if (isChecked) {
                      field.onChange(value.filter((v: string) => v !== item.name));
                    } else {
                      field.onChange([...value, item.name]);
                    }
                  };

                  return (
                    <button
                      type="button"
                      onClick={toggle}
                      className={cn(
                        'cursor-pointer w-full flex items-center gap-[16px] h-max rounded-[8px] border border-[#D9D9D9] px-[12px] py-[8px] shrink-0 mb-2',
                        isChecked && 'border-[#0C8DBF] bg-[#0C8DBF]/10'
                      )}
                    >
                      <label className="relative inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          readOnly
                          className={cn(
                            'peer h-4 w-4 appearance-none border border-[#D9D9D9] rounded-sm shrink-0 shadow-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#0C8DBF]',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            isChecked && 'bg-[#0C8DBF] border-[#0C8DBF] text-white'
                          )}
                        />
                        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                          {isChecked && (
                            <svg
                              className="h-3 w-3 text-white"
                              fill="none"
                              viewBox="0 0 16 16"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8.5l3 3L13 5.5" />
                            </svg>
                          )}
                        </div>
                      </label>

                      <div className="flex flex-col gap-[4px]">
                        <p
                          className={cn(
                            'text-[14px] text-left font-normal text-[#25151E]',
                            isChecked && 'text-[#0C8DBF]'
                          )}
                        >
                          {item?.title}
                        </p>
                        <p
                          className={cn(
                            'text-[12px] text-left font-normal text-[#9D9D9C]',
                            isChecked && 'text-[#0C8DBF]'
                          )}
                        >
                          {item?.name}
                        </p>
                      </div>
                    </button>
                  );
                }}
              />
            ))}
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleCloseModal();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleSubmitForm();
          }}
          disabled={isDisabledButton}
        >
          {mutateAddLocationGoogleReview.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null}
          Add Location
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
AddLocationGoogleReviewModal.displayName = 'AddLocationGoogleReviewModal';
export default AddLocationGoogleReviewModal;
